/**
 * WorkflowOrchestrator — top-level coordinator that wires together the
 * WorkflowEngine, TaskService, NotificationEngine, and DocumentEngine
 * for end-to-end admission / settling-in / onboarding flows.
 */

import { ensureAdmissionAndSettling, ensureWorkflow } from './WorkflowEngine';
import {
  listTasks,
  completionPercent,
  escalateOverdueTasks,
  checkSettlingTransition,
} from './TaskService';
import { WORKFLOW_KEYS } from '../../data/workflowChecklistTemplates';
import { TASK_STATUS } from '../../workflow/taskModel';
import NotificationEngine, {
  NOTIFICATION_TYPE,
  NOTIFICATION_SEVERITY,
  NOTIFICATION_CHANNEL,
} from '../notifications/NotificationEngine';
import DocumentEngine from '../documents/DocumentEngine';

/**
 * Full admission onboarding — called when a resident enters Stage 6.
 * 1. Instantiate Stage 6 + 7A workflow tasks.
 * 2. Generate admission document pack.
 * 3. Emit notification to admissions team.
 */
export function onAdmissionConfirmed({
  residentId,
  facilityId,
  admissionDateIso,
  residentData = {},
  createdBy = 'system',
}) {
  const workflows = ensureAdmissionAndSettling(residentId, facilityId, admissionDateIso, createdBy);

  const docResult = DocumentEngine.generateAdmissionPack(
    residentId,
    facilityId,
    residentData,
    createdBy
  );

  NotificationEngine.create({
    facilityId,
    residentId,
    type: NOTIFICATION_TYPE.SYSTEM,
    severity: NOTIFICATION_SEVERITY.INFO,
    title: 'Admission workflow activated',
    message: `${residentData.residentName || 'Resident'} — Stage 6 and 7A tasks created (${(workflows.stage6?.tasks?.length || 0) + (workflows.stage7a?.tasks?.length || 0)} total). ${docResult.documents?.length || 0} documents generated.`,
    channels: [NOTIFICATION_CHANNEL.IN_APP],
    metadata: { action: 'admission_confirmed', residentId },
  });

  if (process.env.NODE_ENV === 'development') {
    console.debug('[WorkflowOrchestrator] admission confirmed', {
      residentId,
      stage6Tasks: workflows.stage6?.tasks?.length,
      stage7aTasks: workflows.stage7a?.tasks?.length,
      documents: docResult.documents?.length,
    });
  }

  return { workflows, documents: docResult };
}

/**
 * Daily check: escalate overdue tasks across the facility.
 */
export function runDailyEscalation(facilityId, residents = []) {
  const escalated = [];

  for (const resident of residents) {
    const ids = escalateOverdueTasks(resident.id, 'system_daily');
    if (ids.length > 0) {
      escalated.push({ residentId: resident.id, name: resident.name, taskIds: ids });

      NotificationEngine.create({
        facilityId,
        residentId: resident.id,
        type: NOTIFICATION_TYPE.TASK_ESCALATED,
        severity: NOTIFICATION_SEVERITY.CRITICAL,
        title: `${ids.length} task(s) escalated to Facility Manager`,
        message: `${resident.name} — ${ids.length} overdue task(s) escalated to Facility Manager.`,
        channels: [NOTIFICATION_CHANNEL.IN_APP, NOTIFICATION_CHANNEL.EMAIL],
        metadata: { action: 'daily_escalation', taskIds: ids },
      });
    }
  }

  return escalated;
}

/**
 * Check and auto-transition residents from 7A settling-in to 7B steady-state.
 */
export function checkSettlingTransitions(facilityId, residents = []) {
  const transitions = [];

  for (const resident of residents) {
    if (!resident.admissionDate) continue;

    const result = checkSettlingTransition(resident.id, resident.admissionDate);
    if (!result.ready) continue;

    // Instantiate 7B if not already done
    const { created } = ensureWorkflow({
      residentId: resident.id,
      facilityId,
      workflowKey: WORKFLOW_KEYS.STAGE7B_STEADY,
      anchorDateIso: resident.admissionDate,
      createdBy: 'system_auto_transition',
    });

    if (created) {
      NotificationEngine.create({
        facilityId,
        residentId: resident.id,
        type: NOTIFICATION_TYPE.SETTLING_TRANSITION,
        severity: NOTIFICATION_SEVERITY.SUCCESS,
        title: 'Settling-in period complete',
        message: `${resident.name} — auto-transitioned to Stage 7B (Steady State). Reason: ${result.reason === 'all_tasks_completed' ? 'all settling tasks completed' : 'day 28 reached'}.`,
        channels: [NOTIFICATION_CHANNEL.IN_APP],
        metadata: { action: 'settling_transition', reason: result.reason },
      });

      transitions.push({ residentId: resident.id, name: resident.name, reason: result.reason });
    }
  }

  return transitions;
}

/**
 * Facility-wide daily workflow run: escalation + transition + notification scan.
 */
export function runFacilityDailyWorkflow(facilityId, residents = [], staffList = []) {
  const escalations = runDailyEscalation(facilityId, residents);
  const transitions = checkSettlingTransitions(facilityId, residents);
  const alertScan = NotificationEngine.runFullScan(facilityId, staffList);

  if (process.env.NODE_ENV === 'development') {
    console.debug('[WorkflowOrchestrator] daily run', {
      facilityId,
      escalations: escalations.length,
      transitions: transitions.length,
      alerts: {
        acer: alertScan.acer.length,
        overdue: alertScan.overdueTasks.length,
        credentials: alertScan.credentials.length,
      },
    });
  }

  return { escalations, transitions, alertScan };
}

/**
 * Get the full onboarding dashboard data for a single resident.
 */
export function getOnboardingDashboard(residentId, admissionDateIso) {
  const stage6Tasks = listTasks(residentId, WORKFLOW_KEYS.STAGE6_ADMISSION);
  const stage7aTasks = listTasks(residentId, WORKFLOW_KEYS.STAGE7A_SETTLING);
  const stage7bTasks = listTasks(residentId, WORKFLOW_KEYS.STAGE7B_STEADY);

  const stage6Progress = completionPercent(residentId, WORKFLOW_KEYS.STAGE6_ADMISSION);
  const stage7aProgress = completionPercent(residentId, WORKFLOW_KEYS.STAGE7A_SETTLING);

  const settling = admissionDateIso
    ? checkSettlingTransition(residentId, admissionDateIso)
    : null;

  const allTasks = [...stage6Tasks, ...stage7aTasks];
  const overdueTasks = allTasks.filter((t) => t.status === TASK_STATUS.OVERDUE);
  const blockedTasks = allTasks.filter((t) => t.status === TASK_STATUS.BLOCKED);

  const documents = DocumentEngine.getDocuments({ residentId });
  const pendingDocs = documents.filter((d) => d.status !== 'signed' && d.status !== 'archived');

  return {
    residentId,
    stage6: { tasks: stage6Tasks, progress: stage6Progress },
    stage7a: { tasks: stage7aTasks, progress: stage7aProgress },
    stage7b: { tasks: stage7bTasks },
    settling,
    overdueTasks,
    blockedTasks,
    documents,
    pendingDocuments: pendingDocs,
    totalProgress: {
      completed: stage6Progress.completed + stage7aProgress.completed,
      total: stage6Progress.total + stage7aProgress.total,
      percent:
        stage6Progress.total + stage7aProgress.total > 0
          ? Math.round(
              ((stage6Progress.completed + stage7aProgress.completed) /
                (stage6Progress.total + stage7aProgress.total)) *
                100
            )
          : 0,
    },
  };
}
